/* Money that left the exchange, and the question nobody was being asked.
 *
 * The account read writes the free balance straight over whatever the book
 * held. That is right — the exchange is simply the source of what is sitting
 * there — and it means a withdrawal arrives as a balance that is smaller than
 * it was, with nothing to say why. The book's total drops by the amount, the
 * value chart draws it as a fall, and the fall is indistinguishable from a
 * losing week.
 *
 * The record it needs is not a hard one, but it is one only the person who made
 * the transfer can give: money moved out of the exchange either **into another
 * account**, where it is still in the book and must not count as new capital
 * (§5.1 — see `state/convertCash.ts` for why that would destroy every return
 * figure), or **out of the book entirely**, where it is a withdrawal and has to
 * reduce the money paid in. Those are different records and the difference is
 * not inferable from a balance going down.
 *
 * ── what this reports, and what it refuses to claim ──────────────────────
 *
 * A fall in the free balance. That is all. It does not assert a withdrawal
 * happened, because it cannot tell one from a losing trade the exchange settled
 * while the app was closed, and a book that announced "you withdrew $500" on a
 * bad afternoon would be worse than one that said nothing.
 *
 * One case is excluded, and only because it is arithmetic rather than a guess:
 * posting margin moves money from free into a contract and takes nothing off
 * the exchange. The free balance falls and the margin rises by the same amount,
 * so a fall matched by margin going up is explained and is not raised. Without
 * that exclusion this would fire every time a position was opened, which is the
 * fastest way to teach somebody to ignore it.
 *
 * Everything else is handed over as a fact with a question attached. */

export interface ExchangeReading {
  /** Cash sitting free on the exchange. */
  free: number;
  /** Cash posted against open contracts. */
  margin: number;
}

export interface Outflow {
  /** How much the free balance fell. */
  fellBy: number;
  /** How much of that went into margin rather than off the exchange. */
  intoMargin: number;
  /** The part with no arithmetic explanation — the figure worth asking about. */
  unexplained: number;
}

/** Below this it is the exchange's own rounding, or funding paid on a
    contract, and not a transfer anybody made. */
const NOISE = 1;

/** What changed between two readings of the exchange, when it is worth asking
    about. Null on the first read, on a rise, and on a fall that posting margin
    accounts for. */
export function outflowBetween(
  before: ExchangeReading | undefined,
  after: ExchangeReading,
): Outflow | null {
  if (!before) return null;

  const fellBy = before.free - after.free;
  if (!(fellBy > NOISE)) return null;

  /* Margin going up absorbs a fall in free, one for one: the money is still on
     the exchange, standing behind a contract. */
  const intoMargin = Math.max(0, after.margin - before.margin);
  const unexplained = fellBy - intoMargin;
  if (!(unexplained > NOISE)) return null;

  return { fellBy, intoMargin, unexplained };
}
